/**
 * The payroll rules contract (ADR 0011): who owns a rule, who may change which number, how an
 * exception is recorded, what a finalized payroll keeps, and how a rule set is simulated.
 *
 * TWO ROUTERS, DELIBERATELY SEPARATE.
 *
 *   `payrollRulesContract`      — what a TENANT does: read platform rule sets and their versions,
 *                                 resolve what is in force for a legal entity, read the fact
 *                                 catalogue, manage entity CONFIGURATION (draft → publish →
 *                                 archive), manage scoped OVERRIDES, and SIMULATE.
 *   `payrollRulePacksContract`  — how a PLATFORM-OWNED definition changes: draft, validate, publish,
 *                                 archive. Platform operators only, behind the platform token; a
 *                                 tenant session cannot reach it whatever permission it holds.
 *
 * BOTH ROUTERS ARE IN THE ROOT CONTRACT from phase 3c, when their handlers exist. (In 3a they were kept
 * out so the API adapter never carried a router it could not serve.)
 *
 * THE FOUR LAYERS OF A PARAMETER, highest first — the contract names them so a screen and a trace
 * can say where a number came from:
 *   `employment_override` › `legal_entity_override` › `entity_configuration` › `rule_default`
 * A parameter takes part in configuration only if its rule lists it as `configurable`, and in
 * overrides only if it lists it as `overridable`. Neither is implied by being a param.
 *
 * ABSENT IS NOT FALSE. A fact the calculation needs and that was never recorded is `absent`; a fact
 * recorded as `false` is `false`. The two are different states on the wire and the calculation
 * FAILS on the first — it does not default to zero or to false.
 *
 * Money and rates in this contract are decimal STRINGS (CLAUDE.md invariant 2). RN-safe: no Node
 * built-ins.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  COMPANY_COMPONENTS_ORIGIN,
  RULE_CODE_PATTERN,
  RULE_COMPONENT_KINDS,
  PAYROLL_FACT_CLASSES,
  RULE_SECTIONS,
  paramPolicyEntrySchema,
  ruleSetDefinitionSchema,
} from '../schemas/payroll-rules.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

/** `owner` is closed on purpose. Tenant-authored rule sets (phase 6) would add a value. */
export const PAYROLL_RULE_SET_OWNERS = ['platform'] as const;
export const payrollRuleSetOwnerSchema = z.enum(PAYROLL_RULE_SET_OWNERS);
export type PayrollRuleSetOwner = z.infer<typeof payrollRuleSetOwnerSchema>;

export const PAYROLL_RULE_VERSION_STATUSES = ['draft', 'published', 'archived'] as const;
export const payrollRuleVersionStatusSchema = z.enum(PAYROLL_RULE_VERSION_STATUSES);
export type PayrollRuleVersionStatus = z.infer<typeof payrollRuleVersionStatusSchema>;

/** Where a published version sits relative to "today" in the entity's timezone. */
export const payrollRuleTimelineSchema = z.enum(['current', 'scheduled', 'superseded']);

export const payrollRuleCodeSchema = z
  .string()
  .regex(RULE_CODE_PATTERN, 'Codes are UPPER_SNAKE_CASE');
const paramNameSchema = z.string().regex(/^[a-z][a-zA-Z0-9_]*$/);
/** A parameter value, as the language stores it: a decimal string of any length. */
export const payrollRuleParamValueSchema = z.string().regex(/^-?\d+(\.\d+)?$/, 'A decimal number');
/** Full SHA-256, lowercase hex. Never truncated. */
export const payrollSha256Schema = z.string().regex(/^[0-9a-f]{64}$/, 'A full SHA-256 (64 hex)');

/**
 * The stored source of truth for what someone is paid. `Compensation.annualCtc` (ADR 0011, 9); gross,
 * basic and net are CALCULATED facts. The single value is deliberate: another basis is added by
 * deliberate decision, not by a caller passing a different string.
 */
export const PAYROLL_COMPENSATION_BASES = ['ctc'] as const;
export const payrollCompensationBasisSchema = z.enum(PAYROLL_COMPENSATION_BASES);

export const payrollRuleActorSchema = z.object({ id: uuidSchema, name: z.string() });
export type PayrollRuleActor = z.infer<typeof payrollRuleActorSchema>;

const boolQuery = z.enum(['true', 'false']).transform((v) => v === 'true');

// ---------------------------------------------------------------------------
// Errors and diagnostics
// ---------------------------------------------------------------------------

/**
 * Every code a rules route can return in `errorBodySchema.code` or a diagnostic can carry.
 *
 * The first block mirrors the engine's `RuleEngineErrorCode` one to one (a type-level test in
 * `@hrms/payroll` keeps them in step); the second is what only the API can say.
 *
 * HTTP mapping (3c): definition errors → 422; `no_effective_version` / `overlapping_versions` /
 * `retroactive_publish_blocked` / `version_in_use` / `stale_revision` → 409;
 * `self_publish_forbidden` / `self_override_forbidden` / `platform_operator_required` /
 * `override_not_permitted` / `parameter_not_configurable` → 403 (permission-shaped) or 422 (value-shaped),
 * as each route documents; calculation-time errors surface as diagnostics, not as a failed request.
 */
export const PAYROLL_RULE_ERROR_CODES = [
  // ---- engine (definition and configuration time) ----
  'duplicate_code',
  'unknown_reference',
  'unknown_table',
  'unknown_input',
  'unknown_attribute',
  'unknown_flag',
  'unknown_param',
  'circular_dependency',
  'layer_violation',
  'invalid_table',
  'invalid_rule',
  // ---- engine (calculation time) ----
  'missing_input',
  'missing_attribute',
  'missing_table_entry',
  'below_first_band',
  'division_by_zero',
  'no_effective_version',
  'overlapping_versions',
  'override_not_permitted',
  'reverse_unsolvable',
  /**
   * An EXACT solve (ADR 0015) found no input that reaches the target, because the arithmetic is a rounded step
   * function that skips it. Its details name the nearest reachable amounts below and above. Never persisted with
   * a figure: a failed calculation records this code only.
   */
  'target_not_reachable_exactly',
  /**
   * The target is below what any package meeting the wage floor can reach (ADR 0015). Its details name the smallest
   * gross, CTC and net that can. Figures are for the API caller who may see them; a persisted failure carries the
   * code alone.
   */
  'package_below_minimum_wage',
  // ---- API only ----
  /**
   * The caller authored the draft they tried to publish AND the organisation has another eligible
   * publisher. With no other eligible publisher the caller may self-publish instead (`selfPublished`).
   */
  'self_publish_forbidden',
  /** An override of one's own employment. */
  'self_override_forbidden',
  /** A publish whose window a finalized run already covers. */
  'retroactive_publish_blocked',
  /** A configuration value for a param its rule does not list as `configurable`. */
  'parameter_not_configurable',
  /** A value outside the rule's declared param policy: a `statutory` param, or a `statutory_bounded` one out of bounds. */
  'parameter_out_of_policy',
  /** A rule tagged `constraint:<key>` evaluated to a non-zero violation amount (simulation). */
  'constraint_violated',
  /**
   * A stored company value (configuration or override) that the rule version now in force no longer lets a company set: reported
   * as a finding, never as an unresolvable entity (ADR 0012).
   */
  'orphaned_company_value',
  /** A published or archived version cannot be edited. */
  'version_not_editable',
  /** An archive or unpublish of a version a finalized snapshot references. */
  'version_in_use',
  /** `expectedRevision` did not match. */
  'stale_revision',
  /** A definition route called by a tenant actor. */
  'platform_operator_required',
  /** A definition written in a language version the engine does not implement. */
  'unsupported_language_version',
] as const;
export const payrollRuleErrorCodeSchema = z.enum(PAYROLL_RULE_ERROR_CODES);
export type PayrollRuleErrorCode = z.infer<typeof payrollRuleErrorCodeSchema>;

export const payrollRuleDiagnosticSchema = z.object({
  severity: z.enum(['error', 'warning']),
  code: payrollRuleErrorCodeSchema,
  message: z.string(),
  /** The rule the finding is about, when it is about one. */
  rule: payrollRuleCodeSchema.nullable(),
  /** The cycle, in order (`A, B, C, A`), for `circular_dependency`. */
  path: z.array(z.string()).nullable(),
});
export type PayrollRuleDiagnostic = z.infer<typeof payrollRuleDiagnosticSchema>;

// ---------------------------------------------------------------------------
// Facts: what a calculation reads, where it came from, and whether it is there
// ---------------------------------------------------------------------------

export const PAYROLL_FACT_KINDS = ['input', 'attribute', 'flag'] as const;
export const payrollFactKindSchema = z.enum(PAYROLL_FACT_KINDS);

export const PAYROLL_FACT_VALUE_TYPES = ['decimal', 'string', 'boolean'] as const;

/**
 * Where a fact comes from. `solved` is a value the ENGINE produces (a reverse solve's unknown);
 * `supplied` is a value a simulator caller typed in. Everything else is a stored record.
 */
export const PAYROLL_FACT_SOURCES = [
  'compensation',
  'pay_calendar',
  'attendance',
  'entity_configuration',
  'employee_payroll_attributes',
  /** A recorded fact about the person (their gender), not about the employment. Never inferred or defaulted. */
  'person_record',
  'tax_election',
  'tax_declaration',
  'year_to_date',
  'solved',
  'supplied',
  /** An explicit, effective-dated entity-level default (ADR 0012). Never inferred. */
  'entity_default',
  /** A figure read from the legacy run input snapshot (shadow mode, ADR 0012). */
  'legacy_snapshot',
] as const;
export const payrollFactSourceSchema = z.enum(PAYROLL_FACT_SOURCES);
export type PayrollFactSource = z.infer<typeof payrollFactSourceSchema>;

/** One entry of the fact catalogue: what a rule set declares it needs. */
export const payrollFactDefinitionSchema = z.object({
  name: paramNameSchema,
  kind: payrollFactKindSchema,
  valueType: z.enum(PAYROLL_FACT_VALUE_TYPES),
  /** Where it normally comes from, so a screen can say what to fix when it is absent. */
  source: payrollFactSourceSchema,
  /**
   * True: the calculation fails without it. False only where a DECLARED `default` exists. There is
   * no third reading in which absence quietly becomes zero or false.
   */
  required: z.boolean(),
  default: z.union([z.string(), z.boolean()]).nullable(),
  description: z.string(),
  /** The class the rule set declares for this fact (ADR 0016). ABSENT for a fact no rule set classifies. */
  class: z.enum(PAYROLL_FACT_CLASSES).optional(),
  /** The rule set's own statement of whether an entity default may supply it. ABSENT: the source decides, as it always did. */
  defaultable: z.boolean().optional(),
});
export type PayrollFactDefinition = z.infer<typeof payrollFactDefinitionSchema>;

/** `present` was recorded; `defaulted` used a declared default; `absent` is not there. */
export const payrollFactStateSchema = z.enum(['present', 'defaulted', 'absent']);

/**
 * A fact as one calculation saw it. `value` is null exactly when the fact is `absent`; a recorded
 * `false` is `value: false`, never null.
 */
export const payrollFactProvenanceSchema = z
  .object({
    name: paramNameSchema,
    kind: payrollFactKindSchema,
    state: payrollFactStateSchema,
    value: z.union([z.string(), z.boolean()]).nullable(),
    source: payrollFactSourceSchema,
    /** The record it came from: a compensation id, a declaration revision. */
    sourceRef: z.string().nullable(),
    capturedAt: instantSchema.nullable(),
  })
  .refine((f) => (f.state === 'absent') === (f.value === null), {
    message: 'value is null if and only if the fact is absent',
  });
export type PayrollFactProvenance = z.infer<typeof payrollFactProvenanceSchema>;

/**
 * Facts a simulator caller supplies. A key that is omitted — or given as `null` — is ABSENT, which is
 * not `false`. `null` and omission mean the same thing and the handler drops nulls.
 */
export const payrollSuppliedFactsSchema = z.object({
  inputs: z.record(paramNameSchema, payrollRuleParamValueSchema.nullable()).default({}),
  attributes: z.record(paramNameSchema, z.string().min(1).max(80).nullable()).default({}),
  flags: z.record(paramNameSchema, z.boolean().nullable()).default({}),
});
export type PayrollSuppliedFacts = z.infer<typeof payrollSuppliedFactsSchema>;

// ---------------------------------------------------------------------------
// Rule sets and versions
// ---------------------------------------------------------------------------

/**
 * A version without its (large) definition. `revision` counts edits to a draft and is what
 * `expectedRevision` guards; `versionNo` counts published versions of the set. `authors` is everyone
 * who created or edited the draft — the set publishing must not contain the publisher.
 */
export const payrollRuleSetVersionSummarySchema = z.object({
  id: uuidSchema,
  ruleSetCode: payrollRuleCodeSchema,
  versionNo: z.number().int().positive(),
  revision: z.number().int().nonnegative(),
  status: payrollRuleVersionStatusSchema,
  /** Null until published. */
  validFrom: dateOnlySchema.nullable(),
  validTo: dateOnlySchema.nullable(),
  timeline: payrollRuleTimelineSchema.nullable(),
  languageVersion: z.number().int().positive(),
  /** SHA-256 of the canonical definition. Null while a draft, fixed forever once published. */
  checksum: payrollSha256Schema.nullable(),
  ruleCount: z.number().int().nonnegative(),
  note: z.string().nullable(),
  authors: z.array(payrollRuleActorSchema),
  publishedBy: payrollRuleActorSchema.nullable(),
  publishedAt: instantSchema.nullable(),
  /**
   * True when the publisher was also an author, allowed only because the organisation had no other
   * eligible publisher (ADR 0011, decision 3b). Recorded, and audited as its own event.
   */
  selfPublished: z.boolean(),
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type PayrollRuleSetVersionSummary = z.infer<typeof payrollRuleSetVersionSummarySchema>;

export const payrollRuleSetVersionSchema = payrollRuleSetVersionSummarySchema.extend({
  definition: ruleSetDefinitionSchema,
});
export type PayrollRuleSetVersion = z.infer<typeof payrollRuleSetVersionSchema>;

export const payrollRuleSetSchema = z.object({
  code: payrollRuleCodeSchema,
  name: z.string(),
  description: z.string().nullable(),
  section: z.enum(RULE_SECTIONS),
  /** ISO 3166-1 alpha-2, or `*`. Data — the engine never reads it. */
  country: z.string().regex(/^([A-Z]{2}|\*)$/),
  jurisdiction: z.string().nullable(),
  owner: payrollRuleSetOwnerSchema,
  /** The version in force on the query's `asOf`, or null. */
  inForce: payrollRuleSetVersionSummarySchema.nullable(),
  latestVersionNo: z.number().int().nonnegative(),
});
export type PayrollRuleSet = z.infer<typeof payrollRuleSetSchema>;

export const listPayrollRuleSetsQuerySchema = z.object({
  /** Narrows to the entity's country and registered jurisdictions, and defaults `asOf` from its timezone. */
  legalEntityId: uuidSchema.optional(),
  section: z.enum(RULE_SECTIONS).optional(),
  country: z
    .string()
    .regex(/^([A-Z]{2}|\*)$/)
    .optional(),
  asOf: dateOnlySchema.optional(),
});

export const getPayrollRuleSetParamsSchema = z.object({ code: payrollRuleCodeSchema });

export const payrollRuleSetDetailSchema = payrollRuleSetSchema.extend({
  versions: z.array(payrollRuleSetVersionSummarySchema),
});

export const payrollRuleVersionParamsSchema = z.object({ versionId: uuidSchema });

// ---- Definition authoring (platform operators) ----------------------------

/** A draft starts from a definition of its own, or forks an existing version's. */
export const createPayrollRuleDraftBodySchema = z.object({
  note: z.string().trim().max(500).optional(),
  content: z.discriminatedUnion('source', [
    z.object({ source: z.literal('definition'), definition: ruleSetDefinitionSchema }),
    z.object({ source: z.literal('fork'), forkFromVersionId: uuidSchema }),
  ]),
});

export const updatePayrollRuleDraftBodySchema = z.object({
  expectedRevision: z.number().int().nonnegative(),
  definition: ruleSetDefinitionSchema,
  note: z.string().trim().max(500).optional(),
});

/**
 * What a TENANT definition-facing validation would need is not what a PLATFORM one needs: a platform
 * operator has no legal entity. A pack version is validated in the composition of the rule sets that
 * would be in force with it on `validFrom` for its own country (and, if it is jurisdiction-scoped, its
 * jurisdiction) — see the resolver. `validFrom` defaults to today.
 */
export const validatePayrollRuleVersionBodySchema = z.object({
  validFrom: dateOnlySchema.optional(),
});

/** One rule in the dependency graph. `order` is the evaluation order. */
export const payrollRuleGraphNodeSchema = z.object({
  code: payrollRuleCodeSchema,
  name: z.string(),
  section: z.enum(RULE_SECTIONS),
  kind: z.enum(RULE_COMPONENT_KINDS),
  ruleSetCode: payrollRuleCodeSchema,
  dependsOn: z.array(payrollRuleCodeSchema),
  order: z.number().int().nonnegative(),
});
export const payrollRuleGraphSchema = z.object({ nodes: z.array(payrollRuleGraphNodeSchema) });
export type PayrollRuleGraph = z.infer<typeof payrollRuleGraphSchema>;

/** A validation that finds problems is a 200 with `valid: false` — the request itself succeeded. */
export const payrollRuleValidationSchema = z.object({
  valid: z.boolean(),
  diagnostics: z.array(payrollRuleDiagnosticSchema),
  /** Null when the composition did not compile. */
  graph: payrollRuleGraphSchema.nullable(),
  checksum: payrollSha256Schema.nullable(),
});
export type PayrollRuleValidation = z.infer<typeof payrollRuleValidationSchema>;

/**
 * `validFrom` becomes the version's effective date and closes its predecessor at `validFrom − 1`.
 * Refused when the caller authored the draft, when the window overlaps another version, and when a
 * finalized run covers it (`retroactive_publish_blocked`).
 */
export const publishPayrollRuleVersionBodySchema = z.object({
  expectedRevision: z.number().int().nonnegative(),
  validFrom: dateOnlySchema,
});

export const archivePayrollRuleVersionBodySchema = z.object({
  reason: z.string().trim().min(3).max(500),
});

// ---------------------------------------------------------------------------
// Entity configuration (tenant): effective-dated values for `configurable` params
// ---------------------------------------------------------------------------

export const payrollRuleParamValueEntrySchema = z.object({
  ruleCode: payrollRuleCodeSchema,
  param: paramNameSchema,
  value: payrollRuleParamValueSchema,
});
export type PayrollRuleParamValueEntry = z.infer<typeof payrollRuleParamValueEntrySchema>;

const uniqueEntries = (entries: readonly PayrollRuleParamValueEntry[]) =>
  new Set(entries.map((e) => `${e.ruleCode}.${e.param}`)).size === entries.length;

const configurationValuesSchema = z
  .array(payrollRuleParamValueEntrySchema)
  .min(1)
  .max(200)
  .refine(uniqueEntries, { message: 'A rule parameter may appear once' });

export const payrollRuleConfigurationSchema = z.object({
  id: uuidSchema,
  legalEntityId: uuidSchema,
  ruleSetCode: payrollRuleCodeSchema,
  versionNo: z.number().int().positive(),
  revision: z.number().int().nonnegative(),
  status: payrollRuleVersionStatusSchema,
  validFrom: dateOnlySchema.nullable(),
  validTo: dateOnlySchema.nullable(),
  timeline: payrollRuleTimelineSchema.nullable(),
  values: z.array(payrollRuleParamValueEntrySchema),
  note: z.string().nullable(),
  authors: z.array(payrollRuleActorSchema),
  publishedBy: payrollRuleActorSchema.nullable(),
  publishedAt: instantSchema.nullable(),
  /**
   * True when the publisher was also an author, allowed only because the organisation had no other
   * eligible publisher (ADR 0011, decision 3b). Recorded, and audited as its own event.
   */
  selfPublished: z.boolean(),
  archivedAt: instantSchema.nullable(),
  createdAt: instantSchema,
});
export type PayrollRuleConfiguration = z.infer<typeof payrollRuleConfigurationSchema>;

export const listPayrollRuleConfigurationQuerySchema = z.object({
  legalEntityId: uuidSchema,
  ruleSetCode: payrollRuleCodeSchema.optional(),
  includeArchived: boolQuery.optional(),
});

export const createPayrollRuleConfigurationBodySchema = z.object({
  legalEntityId: uuidSchema,
  ruleSetCode: payrollRuleCodeSchema,
  values: configurationValuesSchema,
  note: z.string().trim().max(500).optional(),
});

export const updatePayrollRuleConfigurationBodySchema = z.object({
  expectedRevision: z.number().int().nonnegative(),
  values: configurationValuesSchema,
  note: z.string().trim().max(500).optional(),
});

export const payrollRuleConfigurationParamsSchema = z.object({ configurationId: uuidSchema });

// ---------------------------------------------------------------------------
// Overrides (tenant): an exception for a legal entity or for one employment
// ---------------------------------------------------------------------------

/**
 * The contract's terms. The engine says `company` and `employee` internally; the API maps them at
 * its boundary (invariant 4: compliance belongs to the legal entity, a job is an employment).
 */
export const PAYROLL_OVERRIDE_SCOPES = ['legal_entity', 'employment'] as const;
export const payrollRuleOverrideScopeSchema = z.enum(PAYROLL_OVERRIDE_SCOPES);
export type PayrollRuleOverrideScope = z.infer<typeof payrollRuleOverrideScopeSchema>;

export const payrollRuleOverrideSchema = z
  .object({
    id: uuidSchema,
    scope: payrollRuleOverrideScopeSchema,
    legalEntityId: uuidSchema,
    /** Present exactly when `scope` is `employment`. */
    employmentId: uuidSchema.nullable(),
    ruleSetCode: payrollRuleCodeSchema,
    ruleCode: payrollRuleCodeSchema,
    param: paramNameSchema,
    value: payrollRuleParamValueSchema,
    validFrom: dateOnlySchema,
    validTo: dateOnlySchema.nullable(),
    /** Required: an exception with no reason is not reviewable. */
    reason: z.string(),
    createdBy: payrollRuleActorSchema,
    createdAt: instantSchema,
    endedBy: payrollRuleActorSchema.nullable(),
    endedAt: instantSchema.nullable(),
  })
  .refine((o) => (o.scope === 'employment') === (o.employmentId !== null), {
    message: 'employmentId is set if and only if the scope is employment',
  });
export type PayrollRuleOverride = z.infer<typeof payrollRuleOverrideSchema>;

const overrideCommon = {
  legalEntityId: uuidSchema,
  ruleSetCode: payrollRuleCodeSchema,
  ruleCode: payrollRuleCodeSchema,
  param: paramNameSchema,
  value: payrollRuleParamValueSchema,
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable().optional(),
  reason: z.string().trim().min(3).max(500),
};

/**
 * STRICT, deliberately. A default `z.object` silently drops unknown keys, so a `legal_entity` body
 * that also carried an `employmentId` would parse cleanly and create an entity-wide override the
 * caller meant to scope to one person. Refusing the unknown key makes the mistake a 400.
 */
export const createPayrollRuleOverrideBodySchema = z.discriminatedUnion('scope', [
  z.object({ scope: z.literal('legal_entity'), ...overrideCommon }).strict(),
  z
    .object({ scope: z.literal('employment'), employmentId: uuidSchema, ...overrideCommon })
    .strict(),
]);
export type CreatePayrollRuleOverrideBody = z.infer<typeof createPayrollRuleOverrideBodySchema>;

/** An override is never edited or deleted — it is ended, so "what applied in March" stays true. */
export const endPayrollRuleOverrideBodySchema = z.object({
  validTo: dateOnlySchema,
  reason: z.string().trim().min(3).max(500).optional(),
});

export const listPayrollRuleOverridesQuerySchema = z.object({
  legalEntityId: uuidSchema,
  employmentId: uuidSchema.optional(),
  ruleSetCode: payrollRuleCodeSchema.optional(),
  /** Only overrides in force on this date. */
  asOf: dateOnlySchema.optional(),
  includeEnded: boolQuery.optional(),
});

export const payrollRuleOverrideParamsSchema = z.object({ overrideId: uuidSchema });

// ---------------------------------------------------------------------------
// Resolution: what is in force, with every parameter's layer made visible
// ---------------------------------------------------------------------------

export const PAYROLL_PARAMETER_SOURCES = [
  'rule_default',
  'entity_configuration',
  'legal_entity_override',
  'employment_override',
] as const;
export const payrollParameterSourceSchema = z.enum(PAYROLL_PARAMETER_SOURCES);
export type PayrollParameterSource = z.infer<typeof payrollParameterSourceSchema>;

/** One parameter of one rule: its default, what it resolves to, and which layer decided. */
export const payrollRuleParameterSchema = z.object({
  ruleSetCode: payrollRuleCodeSchema,
  ruleCode: payrollRuleCodeSchema,
  param: paramNameSchema,
  default: payrollRuleParamValueSchema,
  effective: payrollRuleParamValueSchema,
  source: payrollParameterSourceSchema,
  /** The configuration or override row that decided it; null for `rule_default`. */
  sourceId: uuidSchema.nullable(),
  configurable: z.boolean(),
  overridable: z.boolean(),
  /**
   * Who owns the value (ADR 0012, "Parameter policy"): the rule's declared class and, for a bounded one, its bounds or
   * choices. ABSENT for a parameter the rule does not classify: nothing is claimed about it. Optional with no default, so a
   * resolution produced before this field existed still parses.
   */
  policy: paramPolicyEntrySchema.optional(),
  /**
   * True when a company may set this value: a tier (`configurable` or `overridable`) says so AND its class is
   * `statutory_bounded` or `company_policy`. Present only alongside `policy`; a `statutory` parameter is always false.
   */
  companyEditable: z.boolean().optional(),
});
export type PayrollRuleParameter = z.infer<typeof payrollRuleParameterSchema>;

// ---------------------------------------------------------------------------
// What a company may set (ADR 0012, "Company-editable options")
// ---------------------------------------------------------------------------

export const PAYROLL_RULE_OPTION_TIERS = ['configuration', 'override'] as const;

export const getPayrollRuleConfigurationOptionsQuerySchema = z.object({
  legalEntityId: uuidSchema,
  /** The date whose rule versions are read. Defaults to today in the entity's timezone. */
  asOf: dateOnlySchema.optional(),
});

/**
 * One parameter a company may set, read from the rule version IN FORCE: nothing here is invented, and a parameter appears
 * only when its rule classifies it `statutory_bounded` or `company_policy` AND a tier lets a company reach it. A `statutory`
 * or unclassified parameter never appears.
 */
export const payrollRuleCompanyOptionSchema = z.object({
  ruleSetCode: payrollRuleCodeSchema,
  ruleCode: payrollRuleCodeSchema,
  param: paramNameSchema,
  default: payrollRuleParamValueSchema,
  /** What the entity's composition resolves to today (entity level: employment overrides are not included). */
  effective: payrollRuleParamValueSchema,
  source: payrollParameterSourceSchema,
  class: z.enum(['statutory_bounded', 'company_policy']),
  min: payrollRuleParamValueSchema.optional(),
  max: payrollRuleParamValueSchema.optional(),
  choices: z.array(payrollRuleParamValueSchema).optional(),
  /** Where a company may set it: as the entity's standing configuration, as an override, or both. */
  tiers: z.array(z.enum(PAYROLL_RULE_OPTION_TIERS)).min(1),
});
export type PayrollRuleCompanyOption = z.infer<typeof payrollRuleCompanyOptionSchema>;

export const payrollRuleConfigurationOptionsSchema = z.object({
  legalEntityId: uuidSchema,
  asOf: dateOnlySchema,
  options: z.array(payrollRuleCompanyOptionSchema),
});
export type PayrollRuleConfigurationOptions = z.infer<typeof payrollRuleConfigurationOptionsSchema>;

export const resolvePayrollRulesQuerySchema = z.object({
  legalEntityId: uuidSchema,
  /** The payroll period end. Defaults to today in the entity's timezone. */
  asOf: dateOnlySchema.optional(),
  /** When set, the resolution includes that employment's overrides. */
  employmentId: uuidSchema.optional(),
});

export const PAYROLL_ORPHAN_REASONS = [
  'rule_missing',
  'param_missing',
  'tier_not_offered',
] as const;

/**
 * A stored company value the rule versions in force no longer accept (ADR 0012, "Orphaned company values"): the rule or param is
 * gone, or no tier lets a company set it any more. The value is NOT used by the composition (the rule default applies) and is
 * reported here, so one stale row never makes the whole entity unresolvable. The row itself is never rewritten.
 */
export const payrollOrphanedCompanyValueSchema = z.object({
  kind: z.enum(['configuration', 'override']),
  /** The configuration or override row's id. */
  sourceId: uuidSchema,
  ruleCode: payrollRuleCodeSchema,
  param: paramNameSchema,
  value: payrollRuleParamValueSchema,
  reason: z.enum(PAYROLL_ORPHAN_REASONS),
  /** For an employment override only. */
  employmentId: uuidSchema.nullable(),
});
export type PayrollOrphanedCompanyValue = z.infer<typeof payrollOrphanedCompanyValueSchema>;

export const payrollRuleResolutionSchema = z.object({
  legalEntityId: uuidSchema,
  asOf: dateOnlySchema,
  country: z.string(),
  languageVersion: z.number().int().positive(),
  compensationBasis: payrollCompensationBasisSchema,
  versions: z.array(
    z.object({
      ruleSetCode: payrollRuleCodeSchema,
      versionId: uuidSchema,
      versionNo: z.number().int().positive(),
      validFrom: dateOnlySchema,
      validTo: dateOnlySchema.nullable(),
      definitionChecksum: payrollSha256Schema,
    }),
  ),
  parameters: z.array(payrollRuleParameterSchema),
  graph: payrollRuleGraphSchema,
  /** Identifies the rules in force — the value a finalized run's snapshot records. */
  rulesChecksum: payrollSha256Schema,
  /**
   * Stored company values the rules in force no longer accept. ABSENT when there are none (so a response without any is exactly
   * what it was before this field existed).
   */
  orphanedValues: z.array(payrollOrphanedCompanyValueSchema).optional(),
});
export type PayrollRuleResolution = z.infer<typeof payrollRuleResolutionSchema>;

export const getPayrollFactsQuerySchema = z.object({
  legalEntityId: uuidSchema,
  asOf: dateOnlySchema.optional(),
});

export const payrollFactCatalogueSchema = z.object({
  asOf: dateOnlySchema,
  compensationBasis: payrollCompensationBasisSchema,
  facts: z.array(payrollFactDefinitionSchema),
});

// ---------------------------------------------------------------------------
// Simulation
// ---------------------------------------------------------------------------

export const payrollRuleTraceSchema = z.object({
  code: payrollRuleCodeSchema,
  name: z.string(),
  section: z.enum(RULE_SECTIONS),
  kind: z.enum(RULE_COMPONENT_KINDS),
  ruleSetCode: payrollRuleCodeSchema,
  status: z.enum(['computed', 'skipped']),
  skippedReason: z.string().nullable(),
  /** Every input, param and earlier rule the rule actually read, with the value it saw. */
  reads: z.array(z.object({ ref: z.string(), value: z.string() })),
  /** Slab bands chosen, `if` branches taken, floors and caps that bit, overrides applied. */
  notes: z.array(z.string()),
  raw: z.string(),
  afterLimits: z.string(),
  /** Rounded, before proration. */
  full: z.string(),
  prorationFactor: z.string().nullable(),
  final: z.string(),
});
export type PayrollRuleTrace = z.infer<typeof payrollRuleTraceSchema>;

export const payrollRuleLineSchema = z.object({
  code: payrollRuleCodeSchema,
  name: z.string(),
  kind: z.enum(RULE_COMPONENT_KINDS),
  section: z.enum(RULE_SECTIONS),
  tags: z.array(z.string()),
  amount: z.string(),
  description: z.string().nullable(),
});

export const payrollRuleSolveSchema = z.object({
  spec: payrollRuleCodeSchema,
  unknown: paramNameSchema,
  solved: z.string(),
  target: z.string(),
  achieved: z.string(),
  residual: z.string(),
  /** False means the nearest reachable input, not a solution. Reported as a warning, never hidden. */
  exact: z.boolean(),
  evaluations: z.number().int().nonnegative(),
  /**
   * Set when the solve met a DISCONTINUITY it could not satisfy exactly and resolved it by a fixed rule (ADR 0015,
   * "Cliffs"): a wage-floor fixed point that jumps over its target (bonus eligibility at its limit, a provident-fund
   * ceiling with no voluntary contribution). Machine-readable, so a run's diagnostics show it instead of hiding it.
   */
  cliff: z
    .object({
      code: z.literal('no_exact_solution_at_discontinuity'),
      /** Which neighbour was chosen: the one on the low side of the jump, or the one on the high side. */
      chosen: z.enum(['below', 'above']),
      below: z.object({ input: z.string(), achieved: z.string() }),
      above: z.object({ input: z.string(), achieved: z.string() }),
    })
    .optional(),
});

/**
 * With `supplied` facts the caller types every input; with `employment` the API resolves real facts
 * for that employment as of `asOf` — which additionally requires `payroll:read` (ADR 0011, 11).
 */
export const simulatePayrollRulesBodySchema = z.object({
  legalEntityId: uuidSchema,
  /** The payroll period end being simulated. */
  asOf: dateOnlySchema,
  definitions: z.discriminatedUnion('use', [
    z.object({ use: z.literal('in_force') }),
    /** The in-force composition with this draft standing in for its own rule set. */
    z.object({ use: z.literal('draft'), draftVersionId: uuidSchema }),
  ]),
  facts: z.discriminatedUnion('source', [
    z.object({ source: z.literal('supplied'), facts: payrollSuppliedFactsSchema }),
    z.object({ source: z.literal('employment'), employmentId: uuidSchema }),
  ]),
  /** Reverse specs to solve first, in order, before the final evaluation (e.g. a wage-floor fixed point). */
  presolve: z.array(payrollRuleCodeSchema).max(5).default([]),
  /** A reverse spec that determines the unknown from a target fact (e.g. CTC → gross). */
  reverse: payrollRuleCodeSchema.nullable().default(null),
  /** Apply the stored configuration and overrides in force. Off shows the rule defaults alone. */
  applyStored: z.boolean().default(true),
});

export const payrollRuleSimulationSchema = z.object({
  asOf: dateOnlySchema,
  languageVersion: z.number().int().positive(),
  engineVersion: z.string(),
  /** Null for a simulation over a draft, which has no checksum until published. */
  rulesChecksum: payrollSha256Schema.nullable(),
  /** Every fact the calculation used or lacked, with its provenance. Absent facts appear as absent. */
  facts: z.array(payrollFactProvenanceSchema),
  values: z.record(z.string()),
  lines: z.array(payrollRuleLineSchema),
  totals: z.record(z.string()),
  trace: z.array(payrollRuleTraceSchema),
  solves: z.array(payrollRuleSolveSchema),
  diagnostics: z.array(payrollRuleDiagnosticSchema),
});
export type PayrollRuleSimulation = z.infer<typeof payrollRuleSimulationSchema>;

// ---------------------------------------------------------------------------
// The rules snapshot a finalized payroll keeps (ADR 0011, 7)
// ---------------------------------------------------------------------------

/**
 * Everything needed to reproduce a calculation's rules, frozen with the run.
 *
 *   `definitionChecksum` = SHA-256 of canonicalJson(definition), per version.
 *   `rulesChecksum`      = SHA-256 of canonicalJson({ languageVersion, definitions (sorted by code),
 *                          configuration, legalEntityOverrides, employmentOverrides }).
 *
 * `engineVersion`, `asOf` and every id travel BESIDE the hash, not inside it: the checksum
 * identifies the rules, the other fields identify the run. Inline definitions are deliberate — the
 * version rows are immutable, but a snapshot that needs a join to be read is one deletion away from
 * being unreproducible. Immutable once persisted; a replay test re-hashes and re-evaluates it.
 */
export const payrollRulesSnapshotSchema = z.object({
  snapshotVersion: z.literal(1),
  legalEntityId: uuidSchema,
  asOf: dateOnlySchema,
  languageVersion: z.number().int().positive(),
  engineVersion: z.string(),
  compensationBasis: payrollCompensationBasisSchema,
  versions: z.array(
    z.object({
      ruleSetCode: payrollRuleCodeSchema,
      versionId: uuidSchema,
      versionNo: z.number().int().positive(),
      validFrom: dateOnlySchema,
      validTo: dateOnlySchema.nullable(),
      definitionChecksum: payrollSha256Schema,
    }),
  ),
  /** The full resolved definitions, inline. */
  definitions: z.array(ruleSetDefinitionSchema),
  configuration: z.array(
    z.object({
      configurationId: uuidSchema,
      ruleSetCode: payrollRuleCodeSchema,
      versionNo: z.number().int().positive(),
      values: z.array(payrollRuleParamValueEntrySchema),
      /**
       * ABSENT for a stored tenant configuration. `legacy_statutory_rule_version` marks a layer derived
       * in memory from a stored statutory rule version (ADR 0012, decision 3): `configurationId` is that
       * row's id, and no configuration row exists.
       */
      origin: z.literal('legacy_statutory_rule_version').optional(),
    }),
  ),
  legalEntityOverrides: z.array(
    z.object({
      overrideId: uuidSchema,
      ruleCode: payrollRuleCodeSchema,
      param: paramNameSchema,
      value: payrollRuleParamValueSchema,
      reason: z.string(),
    }),
  ),
  /** Per employment, so one person's exception is reproducible without touching anyone else's. */
  employmentOverrides: z.array(
    z.object({
      employmentId: uuidSchema,
      overrides: z.array(
        z.object({
          overrideId: uuidSchema,
          ruleCode: payrollRuleCodeSchema,
          param: paramNameSchema,
          value: payrollRuleParamValueSchema,
          reason: z.string(),
        }),
      ),
    }),
  ),
  rulesChecksum: payrollSha256Schema,
  /**
   * DERIVED definitions carried in `definitions` that no published version backs (ADR 0012, "Company components"): the rules the
   * engine derived from the tenant's own components at the lock, frozen inline. Each names its origin and checksum and has NO
   * `versionId`, so a reader never looks for a version row. ABSENT on every snapshot frozen before this existed, which then
   * verifies and replays exactly as it always did; only a NEW snapshot with company components carries it.
   */
  derived: z
    .array(
      z.object({
        ruleSetCode: payrollRuleCodeSchema,
        origin: z.literal(COMPANY_COMPONENTS_ORIGIN),
        definitionChecksum: payrollSha256Schema,
      }),
    )
    .optional(),
});
export type PayrollRulesSnapshot = z.infer<typeof payrollRulesSnapshotSchema>;

// ---------------------------------------------------------------------------
// Routers
// ---------------------------------------------------------------------------

const authErrors = { 401: errorBodySchema, 403: errorBodySchema } as const;

/** The tenant router. Not in the root contract until 3c. */
export const payrollRulesContract = c.router(
  {
    listRuleSets: {
      method: 'GET',
      path: '/rule-sets',
      query: listPayrollRuleSetsQuerySchema,
      responses: {
        200: z.object({ items: z.array(payrollRuleSetSchema) }),
        ...authErrors,
        404: errorBodySchema,
      },
      summary: 'Platform rule sets, with the version in force',
      metadata: routeMeta('payroll.rules:read'),
    },

    getRuleSet: {
      method: 'GET',
      path: '/rule-sets/:code',
      pathParams: getPayrollRuleSetParamsSchema,
      responses: { 200: payrollRuleSetDetailSchema, ...authErrors, 404: errorBodySchema },
      summary: 'One rule set and its published version history',
      metadata: routeMeta('payroll.rules:read'),
    },

    getVersion: {
      method: 'GET',
      path: '/versions/:versionId',
      pathParams: payrollRuleVersionParamsSchema,
      responses: { 200: payrollRuleSetVersionSchema, ...authErrors, 404: errorBodySchema },
      summary: 'One published version with its full definition (read-only to tenants)',
      metadata: routeMeta('payroll.rules:read'),
    },

    getFacts: {
      method: 'GET',
      path: '/facts',
      query: getPayrollFactsQuerySchema,
      responses: { 200: payrollFactCatalogueSchema, ...authErrors, 404: errorBodySchema },
      summary: 'The facts the rules in force need, and where each comes from',
      metadata: routeMeta('payroll.rules:read'),
    },

    resolve: {
      method: 'GET',
      path: '/resolve',
      query: resolvePayrollRulesQuerySchema,
      responses: {
        200: payrollRuleResolutionSchema,
        ...authErrors,
        404: errorBodySchema,
        /** No version of a rule set is in force on `asOf`, or two are. */
        409: errorBodySchema,
        /** The composition does not compile. */
        422: errorBodySchema,
      },
      summary: 'What is in force for a legal entity on a date, with every parameter’s layer',
      metadata: routeMeta(['payroll.rules:read', 'payroll:read']),
    },

    // ---- Entity configuration -------------------------------------------------

    listConfiguration: {
      method: 'GET',
      path: '/configuration',
      query: listPayrollRuleConfigurationQuerySchema,
      responses: {
        200: z.object({ items: z.array(payrollRuleConfigurationSchema) }),
        ...authErrors,
        404: errorBodySchema,
      },
      summary: "A legal entity's rule configuration versions",
      metadata: routeMeta('payroll.rules:read'),
    },

    getConfigurationOptions: {
      method: 'GET',
      path: '/configuration/options',
      query: getPayrollRuleConfigurationOptionsQuerySchema,
      responses: {
        200: payrollRuleConfigurationOptionsSchema,
        ...authErrors,
        404: errorBodySchema,
        /** No version of a rule set is in force on `asOf`, or two are. */
        409: errorBodySchema,
        /** The composition does not compile. */
        422: errorBodySchema,
      },
      summary:
        'What a company may set for a legal entity: the classified, reachable parameters of the rules in force',
      metadata: routeMeta('payroll.rules:read'),
    },

    createConfigurationDraft: {
      method: 'POST',
      path: '/configuration',
      headers: idempotencyHeadersSchema,
      body: createPayrollRuleConfigurationBodySchema,
      responses: {
        201: payrollRuleConfigurationSchema,
        ...authErrors,
        404: errorBodySchema,
        /** A value for a param the rule does not list as `configurable` → `parameter_not_configurable`. */
        422: errorBodySchema,
      },
      summary: 'Start a configuration draft for a legal entity',
      metadata: routeMeta('payroll.rules:manage'),
    },

    updateConfigurationDraft: {
      method: 'PUT',
      path: '/configuration/:configurationId',
      pathParams: payrollRuleConfigurationParamsSchema,
      body: updatePayrollRuleConfigurationBodySchema,
      responses: {
        200: payrollRuleConfigurationSchema,
        ...authErrors,
        404: errorBodySchema,
        /** `stale_revision`, or the version is no longer a draft (`version_not_editable`). */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Edit a configuration draft (records the caller as an author)',
      metadata: routeMeta('payroll.rules:manage'),
    },

    validateConfiguration: {
      method: 'POST',
      path: '/configuration/:configurationId/validate',
      pathParams: payrollRuleConfigurationParamsSchema,
      body: c.noBody(),
      responses: { 200: payrollRuleValidationSchema, ...authErrors, 404: errorBodySchema },
      summary:
        'Check a configuration draft against the rules in force (an author may do this alone)',
      metadata: routeMeta('payroll.rules:manage', { ai: 'read' }),
    },

    publishConfiguration: {
      method: 'POST',
      path: '/configuration/:configurationId/publish',
      pathParams: payrollRuleConfigurationParamsSchema,
      headers: idempotencyHeadersSchema,
      body: publishPayrollRuleVersionBodySchema,
      responses: {
        200: payrollRuleConfigurationSchema,
        ...authErrors,
        404: errorBodySchema,
        /** `stale_revision`, `overlapping_versions`, `retroactive_publish_blocked`. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Publish a configuration draft — always re-validates, whatever was validated before',
      metadata: routeMeta('payroll.rules:publish'),
    },

    archiveConfiguration: {
      method: 'POST',
      path: '/configuration/:configurationId/archive',
      pathParams: payrollRuleConfigurationParamsSchema,
      body: archivePayrollRuleVersionBodySchema,
      responses: {
        200: payrollRuleConfigurationSchema,
        ...authErrors,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Archive a configuration version that is not in force and not referenced by a finalized run',
      metadata: routeMeta('payroll.rules:publish'),
    },

    // ---- Overrides ------------------------------------------------------------

    listOverrides: {
      method: 'GET',
      path: '/overrides',
      query: listPayrollRuleOverridesQuerySchema,
      responses: {
        200: z.object({ items: z.array(payrollRuleOverrideSchema) }),
        ...authErrors,
        404: errorBodySchema,
      },
      summary: 'Overrides for a legal entity or an employment',
      metadata: routeMeta('payroll.rules:read'),
    },

    createOverride: {
      method: 'POST',
      path: '/overrides',
      headers: idempotencyHeadersSchema,
      body: createPayrollRuleOverrideBodySchema,
      responses: {
        201: payrollRuleOverrideSchema,
        ...authErrors,
        404: errorBodySchema,
        /** `overlapping_versions` — another override of the same parameter is in force in that window. */
        409: errorBodySchema,
        /** `override_not_permitted` (the rule does not list the param as `overridable`). */
        422: errorBodySchema,
      },
      summary: 'Record an override of one parameter, effective-dated',
      metadata: routeMeta('payroll.rules.override:manage'),
    },

    endOverride: {
      method: 'POST',
      path: '/overrides/:overrideId/end',
      pathParams: payrollRuleOverrideParamsSchema,
      body: endPayrollRuleOverrideBodySchema,
      responses: {
        200: payrollRuleOverrideSchema,
        ...authErrors,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'End an override on a date — overrides are never edited or deleted',
      metadata: routeMeta('payroll.rules.override:manage'),
    },

    // ---- Simulation -----------------------------------------------------------

    simulate: {
      method: 'POST',
      path: '/simulate',
      body: simulatePayrollRulesBodySchema,
      responses: {
        200: payrollRuleSimulationSchema,
        ...authErrors,
        404: errorBodySchema,
        /** The composition does not compile; the diagnostics say why. */
        422: errorBodySchema,
      },
      summary: 'Run the rules over supplied or resolved facts and return the trace',
      // `payroll.rules:read` is always required. `payroll:read` is ADDITIONALLY required when
      // `facts.source` is `employment` or `resolve` names an employment: the handler enforces the
      // exact rule, and the declaration is the any-of union of every guard the handler contains —
      // that is what the route-permission drift test holds it to (apps/api/src/lib/route-permissions).
      // AI-hidden: a non-GET would otherwise be offered to the copilot as a write proposal.
      metadata: routeMeta(['payroll.rules:read', 'payroll:read'], { ai: 'hidden', salaryPin: 'subject' }),
    },
  },
  { pathPrefix: '/payroll/rules' },
);

/**
 * The platform-operator router.
 *
 * AUTHENTICATED BY THE PLATFORM-ADMINISTRATOR TOKEN, WHICH A TENANT TOKEN CAN NEVER SATISFY, and
 * therefore `routeMeta(null)`: a platform administrator is not a Person and holds no grants, so there
 * is no tenant permission to declare and none is added (ADR 0011, decision 1). The manage / publish
 * split that tenants have for configuration is replaced here by the rule that matters — author ≠
 * publisher, with a recorded exception when there is no other active administrator.
 * `ai: 'hidden'`: the copilot acts as a tenant person and must never be offered these.
 */
export const payrollRulePacksContract = c.router(
  {
    createDraftVersion: {
      method: 'POST',
      path: '/rule-sets/:code/versions',
      pathParams: getPayrollRuleSetParamsSchema,
      body: createPayrollRuleDraftBodySchema,
      responses: {
        201: payrollRuleSetVersionSchema,
        ...authErrors,
        404: errorBodySchema,
        /** `platform_operator_required` is a 403; an invalid definition is a 422. */
        422: errorBodySchema,
      },
      summary: 'Start a draft version of a platform rule set',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },

    updateDraftVersion: {
      method: 'PUT',
      path: '/versions/:versionId',
      pathParams: payrollRuleVersionParamsSchema,
      body: updatePayrollRuleDraftBodySchema,
      responses: {
        200: payrollRuleSetVersionSchema,
        ...authErrors,
        404: errorBodySchema,
        /** `stale_revision` or `version_not_editable`. */
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary: 'Edit a draft version (records the caller as an author)',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },

    validateVersion: {
      method: 'POST',
      path: '/versions/:versionId/validate',
      pathParams: payrollRuleVersionParamsSchema,
      body: validatePayrollRuleVersionBodySchema,
      responses: { 200: payrollRuleValidationSchema, ...authErrors, 404: errorBodySchema },
      summary:
        'Compile a draft in the composition in force for an entity (an author may do this alone)',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },

    publishVersion: {
      method: 'POST',
      path: '/versions/:versionId/publish',
      pathParams: payrollRuleVersionParamsSchema,
      body: publishPayrollRuleVersionBodySchema,
      responses: {
        200: payrollRuleSetVersionSchema,
        ...authErrors,
        404: errorBodySchema,
        /** `stale_revision`, `overlapping_versions`, `retroactive_publish_blocked`. */
        409: errorBodySchema,
        /** The composition does not compile, or the language version is unsupported. */
        422: errorBodySchema,
      },
      summary: 'Publish a draft, closing the version it supersedes — always re-validates',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },

    archiveVersion: {
      method: 'POST',
      path: '/versions/:versionId/archive',
      pathParams: payrollRuleVersionParamsSchema,
      body: archivePayrollRuleVersionBodySchema,
      responses: {
        200: payrollRuleSetVersionSchema,
        ...authErrors,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Archive a version that is not in force and not referenced by a finalized run',
      metadata: routeMeta(null, { ai: 'hidden' }),
    },
  },
  { pathPrefix: '/payroll/rule-packs' },
);
